import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  TextField,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const schema = z.object({
  appReviewRequestPlacement: z.coerce
    .number({
      required_error: 'Required',
      invalid_type_error: 'Must be a number',
    })
    .int()
    .min(0, { message: 'Must be >= 0' }),
});

export const AppReviewRequestPlacement = () => {
  const [updateConfig, { isLoading: isSaving }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const {
    remoteAppConfig: { appReviewRequestPlacement },
  } = useGetAppConfig();

  const { onSettled } = useOnSettled();

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(schema as any),
    defaultValues: {
      appReviewRequestPlacement: appReviewRequestPlacement ?? 4,
    },
  });

  useEffect(() => {
    setValue(
      'appReviewRequestPlacement',
      appReviewRequestPlacement ?? 4,
    );
  }, [appReviewRequestPlacement, setValue]);

  const doSubmit = useCallback(
    async (data: any) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data,
        },
        {
          onSettled,
        },
      );

      reset(data);
    },
    [updateConfig, onSettled, reset],
  );

  const onSubmit = handleSubmit(doSubmit);

  return (
    <Box marginTop="2rem">
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        marginBottom={1}
      >
        <Typography variant="h6">
          App Review Request Placement
        </Typography>
        <Tooltip title="Number indicating when to request app review from users.">
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <TextField
            label="Review Request Placement"
            disabled={isSaving}
            error={!!formState.errors.appReviewRequestPlacement}
            type="number"
            InputLabelProps={{
              shrink: true,
              sx: { paddingBottom: '2rem' },
            }}
            {...register('appReviewRequestPlacement', {
              required: true,
            })}
          />
          {(formState.isDirty || formState.isSubmitting) && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSaving || !formState.isValid}
              endIcon={isSaving && <CircularProgress size={14} />}
            >
              {isSaving ? 'Updating' : 'Update'} App Review Request
              Placement
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};

